/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/require-default-props */
import React, {
  useMemo,
} from 'react';
import PropTypes from 'prop-types';

import TagInput from '../../../common/inputs/TagInput/TagInput';

import { getIdMap } from '../../../helpers/helpers';

import { getFormulaError } from './formFieldsHelpers';

export default function CalculationDropdown({
  id,
  sectionId,
  onTagsAndFormulaChange,
  formula,
  tags,
  sections = [],
  calculationColumn,
  hideExplanation,
}) {
  const flatFields = useMemo(() => (
    sections.map(({ fields: sectionFields = [], name, id: sId }) => (
      // Include same table for calculation columns
      sectionFields.filter((field) => field.id !== id || calculationColumn).map((field = {}) => {
        const { configProps: { title } = {} } = field;
        return { ...field, name: `${name} - ${title}`, sectionId: sId };
      })
    )).flat()
  ), [sections, id, calculationColumn]);
  const fieldMap = useMemo(() => getIdMap(flatFields), [flatFields]);

  const errorMessage = useMemo(() => (
    getFormulaError({
      configProps: { formula, tags },
      sectionId,
      field: { id },
      sections,
      calculationColumn,
    })
  ), [
    id,
    sectionId,
    formula,
    tags,
    sections,
    calculationColumn,
  ]);

  const whitelist = useMemo(() => (
    sections.reduce((acc, section) => {
      const { name: sectionName, fields } = section ?? {};
      fields?.forEach((field) => {
        const {
          configProps: { title, columns = [] } = {},
          id: fieldId,
          selectedType,
        } = field ?? {};

        if (!title) return;
        const prefix = `${sectionName} - ${title.trim()}`;

        // Include same table for calculation columns
        if (fieldId === id && !calculationColumn) return;
        if (selectedType !== 'table') {
          acc.push({
            value: prefix,
            key: fieldId,
            editable: false,
          });
          return;
        }
        const isTableCalculation = calculationColumn && fieldId === id;
        const filteredColumns = isTableCalculation
          // Only allow one level deep
          ? columns.filter(({ isCalculation }) => !isCalculation)
          : columns;
        filteredColumns.forEach((col) => {
          if (!col.name) return;
          const trimmed = col.name.trim();
          const suffix = isTableCalculation ? trimmed : `Sum of ${trimmed} values`;
          const value = `${prefix} - ${suffix}`;
          acc.push({
            value,
            key: `${fieldId}/${col.key}`,
            editable: false,
          });
        });
      });
      return acc;
    }, [])
  ), [sections, id, calculationColumn]);

  return (
    <div className="form-calculation-container">
      <TagInput
        id={id}
        calculationColumn={calculationColumn}
        sections={sections}
        fieldMap={fieldMap}
        formula={formula}
        tags={tags}
        onTagsAndFormulaChange={onTagsAndFormulaChange}
        whitelist={whitelist}
      />
      {errorMessage && (
        <div id="form-calculation-error">
          {errorMessage}
        </div>
      )}
      {!hideExplanation && (
        <div className="form-calculation-subtext">
          Add a formula (e.g. 2 + 3).
          <br />
          <br />
          Use <b>#</b> to insert fields into the equation
        </div>
      )}
    </div>
  );
}

CalculationDropdown.propTypes = {
  id: PropTypes.string.isRequired,
  sectionId: PropTypes.string.isRequired,
  onTagsAndFormulaChange: PropTypes.func.isRequired,
  formula: PropTypes.string.isRequired,
  tags: PropTypes.array,
  sections: PropTypes.array,
  calculationColumn: PropTypes.string.isRequired,
  hideExplanation: PropTypes.bool,
};
